import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'singlenumber', label: 'Single Number (XOR)' },
  { key: 'countingbits', label: 'Counting Bits' },
  { key: 'missingnumber', label: 'Missing Number' },
  { key: 'happynumber', label: 'Happy Number' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

const BoxRow = ({ label, values, getBoxState }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--muted)', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', minHeight: 46 }}>
      {values.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--muted)', padding: '12px 0' }}>empty</span>}
      {values.map((v, i) => {
        const st = getBoxState ? getBoxState(i) : {};
        return (
          <div key={i} style={{
            width: 44, height: 44, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 800, fontFamily: "'Inter',sans-serif",
            background: st.bg || 'var(--bg3)', color: st.color || 'var(--text)',
            border: `1.5px solid ${st.border || 'var(--border)'}`, transition: 'all 0.2s ease',
          }}>{v}</div>
        );
      })}
    </div>
  </div>
);

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 220 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={{
      padding: '8px 14px', borderRadius: 9, cursor: step === 0 ? 'default' : 'pointer',
      border: `1.5px solid ${step === 0 ? 'var(--border)' : V1}`, background: 'transparent',
      color: step === 0 ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
      fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: step === 0 ? 0.6 : 1,
    }}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);
const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);

/* ─── Single Number (XOR) ─────────────────────────────────────────── */
const SingleNumberMode = () => {
  const [input, setInput] = useState('4, 1, 2, 1, 2');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const nums = input.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v));
    if (nums.length === 0 || nums.length > 10) return;
    const built = [];
    let acc = 0;
    nums.forEach((n, i) => {
      const before = acc;
      acc = acc ^ n;
      built.push({ i, acc, note: `result = ${before} ^ ${n} = ${acc}.` });
    });
    built.push({ done: true, acc, note: `Every number that appeared TWICE cancelled itself out (x ^ x = 0). What's left is the single number: ${acc}.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const nums = input.split(',').map(s => s.trim()).filter(Boolean);
  const getBoxState = i => (cur && !cur.done && cur.i === i) ? { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' } : {};

  const code = {
    lines: [
      'int singleNumber(int[] nums) {',
      '    int result = 0;',
      '    for (int n : nums) {',
      '        result ^= n;',
      '    }',
      '    return result;',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 5 : 3,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="nums" values={nums} getBoxState={getBoxState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="4, 1, 2, 1, 2 (max 10)" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter numbers where every value appears twice except one, then Run.` : cur.note}
          {cur && cur.acc !== undefined && <> <Chip color={TEAL}>result = {cur.acc}</Chip></>}
        </p>
      </div>
      <CodePanel title="Java — Single Number (XOR)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Counting Bits ───────────────────────────────────────────────── */
const CountingBitsMode = () => {
  const [n, setN] = useState(5);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const size = Math.min(15, Math.max(0, n));
    const dp = new Array(size + 1).fill(0);
    const built = [{ dp: [...dp], i: 0, note: 'dp[0] = 0 — the number 0 has zero set bits.' }];
    for (let i = 1; i <= size; i++) {
      dp[i] = dp[i >> 1] + (i & 1);
      built.push({ dp: [...dp], i, from: i >> 1, note: `dp[${i}] = dp[${i} >> 1] + (${i} & 1) = dp[${i >> 1}] + ${i & 1} = ${dp[i >> 1]} + ${i & 1} = ${dp[i]}.` });
    }
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const size = Math.min(15, Math.max(0, n));
  const getBoxState = i => {
    if (!cur) return {};
    if (i === cur.i) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
    if (cur.from === i) return { bg: `${TEAL}22`, border: TEAL, color: 'var(--text)' };
    return {};
  };

  const code = {
    lines: [
      'int[] countBits(int n) {',
      '    int[] dp = new int[n + 1];',
      '    for (int i = 1; i <= n; i++) {',
      '        dp[i] = dp[i >> 1] + (i & 1);',
      '    }',
      '    return dp;',
      '}',
    ],
    active: !cur ? -1 : cur.i === 0 ? -1 : 3,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label={`dp[0..${size}] — set-bit count for each number`} values={cur ? cur.dp : new Array(size + 1).fill(0)} getBoxState={getBoxState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>n</span>
          <input value={n} onChange={e => setN(Math.min(15, Math.max(0, parseInt(e.target.value, 10) || 0)))} style={{ ...smallInput, width: 60 }} type="number" min={0} max={15} />
          <button onClick={run} style={smallBtn(V1)}>Build Table</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Choose n (0-15), then Build Table — each dp[i] reuses the answer for i >> 1.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Counting Bits (DP)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Missing Number ──────────────────────────────────────────────── */
const MissingNumberMode = () => {
  const [input, setInput] = useState('3, 0, 1');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const nums = input.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v));
    if (nums.length === 0 || nums.length > 10) return;
    const n = nums.length;
    const expected = (n * (n + 1)) / 2;
    const actual = nums.reduce((a, b) => a + b, 0);
    const built = [
      { phase: 'expected', vars: { expected }, note: `n = ${n}. expectedSum = n(n+1)/2 = ${n}×${n + 1}/2 = ${expected} — the sum of every number from 0 to ${n} if none were missing.` },
      { phase: 'actual', vars: { expected, actual }, note: `actualSum = sum(nums) = ${nums.join(' + ')} = ${actual}.` },
      { phase: 'done', done: true, vars: { expected, actual, missing: expected - actual }, note: `missing = expectedSum − actualSum = ${expected} − ${actual} = ${expected - actual}.` },
    ];
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const nums = input.split(',').map(s => s.trim()).filter(Boolean);

  const code = {
    lines: [
      'int missingNumber(int[] nums) {',
      '    int n = nums.length;',
      '    int expectedSum = n * (n + 1) / 2;',
      '    int actualSum = 0;',
      '    for (int x : nums) actualSum += x;',
      '    return expectedSum - actualSum;',
      '}',
    ],
    active: !cur ? -1 : cur.phase === 'expected' ? 2 : cur.phase === 'actual' ? 4 : 5,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label={`nums (should contain 0..${nums.length}, one missing)`} values={nums} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="3, 0, 1 (max 10)" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter n numbers drawn from 0..n with exactly one missing, then Run — the math (sum-formula) approach.` : cur.note}
          {cur && cur.vars && Object.entries(cur.vars).map(([k, v]) => <Chip key={k} color={k === 'missing' ? GREEN : TEAL}>{k} = {v}</Chip>)}
        </p>
      </div>
      <CodePanel title="Java — Missing Number (Math: Sum Formula)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Happy Number ────────────────────────────────────────────────── */
const HappyNumberMode = () => {
  const [input, setInput] = useState('19');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    let cur = Number(input);
    if (isNaN(cur) || cur < 1 || cur > 999999999) return;
    const seen = new Set();
    const built = [];
    let iterations = 0;
    while (cur !== 1 && !seen.has(cur) && iterations < 15) {
      seen.add(cur);
      const digits = String(cur).split('').map(Number);
      const next = digits.reduce((sum, d) => sum + d * d, 0);
      built.push({ cur, digits, next, note: `${cur}: ${digits.map(d => `${d}²`).join(' + ')} = ${next}.` });
      cur = next;
      iterations++;
    }
    if (cur === 1) {
      built.push({ cur: 1, done: true, happy: true, note: `Reached 1 — this number IS happy!` });
    } else {
      built.push({ cur, done: true, happy: false, note: `${cur} was already seen earlier in this sequence — a cycle! This number is NOT happy.` });
    }
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;

  const code = {
    lines: [
      'boolean isHappy(int n) {',
      '    Set<Integer> seen = new HashSet<>();',
      '    while (n != 1 && !seen.contains(n)) {',
      '        seen.add(n);',
      '        n = sumOfSquaredDigits(n);',
      '    }',
      '    return n == 1;',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 6 : 4,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={{ ...smallInput, width: 100 }} placeholder="19" type="number" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter a positive number, then Run — watch the digit-square-sum sequence, using a set to catch cycles.` : cur.note}
        </p>
        {cur && cur.happy !== undefined && (
          <p style={{ marginTop: 10, fontWeight: 800, color: cur.happy ? GREEN : RED }}>{cur.happy ? 'Happy!' : 'Not happy.'}</p>
        )}
      </div>
      <CodePanel title="Java — Happy Number (HashSet Cycle Detection)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const buildQuizScenario = () => {
  const isPow2 = Math.random() < 0.5;
  const n = isPow2 ? Math.pow(2, Math.floor(Math.random() * 8)) : (2 + Math.floor(Math.random() * 500)) | 1;
  const actualIsPow2 = n > 0 && (n & (n - 1)) === 0;
  return { n, correct: actualIsPow2 ? 'yes' : 'no' };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  const options = [
    { key: 'yes', label: `Yes — n & (n-1) == 0` },
    { key: 'no', label: `No — n & (n-1) != 0` },
  ];

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '4px 0 14px', lineHeight: 1.6 }}>
        Is <span style={{ fontFamily: 'monospace', color: V2 }}>n = {scenario.n}</span> a power of two?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {options.map(o => {
          const revealed = answer !== null;
          const isCorrect = o.key === scenario.correct;
          const isPicked = o.key === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={o.key} onClick={() => pick(o.key)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{o.label}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text)' }}>
            A power of two has exactly ONE set bit — subtracting 1 flips every bit below it, so ANDing the two always gives 0.
          </span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const MathBitPlayground = () => {
  const [mode, setMode] = useState('singlenumber');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'singlenumber' && <SingleNumberMode />}
      {mode === 'countingbits' && <CountingBitsMode />}
      {mode === 'missingnumber' && <MissingNumberMode />}
      {mode === 'happynumber' && <HappyNumberMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default MathBitPlayground;
